import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { Alert, Box, CircularProgress, Slider, Stack, Typography } from '@mui/material';

import {
    eventExtended,
    familyExtended,
    fetchAncestorFamilies,
    fetchAncestorPersons,
    fetchEventsByHandles,
    fetchPersonByGrampsId,
    handlesDigest,
    personExtended,
    restPersonName,
    type RestEvent,
    type RestFamily,
    type RestPerson,
} from '../api/grampsRest';
import { readBearerToken, subscribeToBearerToken } from '../grampsAuth';
import { Geocoding } from '../components/Geocoding';
import { MapTiles } from '../components/MapTiles';
import { boundsOfFeatures, curvedStepFeatures } from '../components/mapCurves';
import { PersonContextMenu } from '../components/PersonContextMenu';
import { useViewRoute } from '../viewRoute';

type Coordinates = [number, number];

interface AncestryEvent {
    event: RestEvent;
    owner: RestPerson;
    coordinates: Coordinates;
    year: number;
}

function coordinatesFor(event: RestEvent): Coordinates | undefined {
    const place = eventExtended(event).place;
    const longitude = Number.parseFloat(place?.long ?? '');
    const latitude = Number.parseFloat(place?.lat ?? '');
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return undefined;
    if (longitude < -180 || longitude > 180 || latitude < -90 || latitude > 90) return undefined;
    return [longitude, latitude];
}

function startYear(event: RestEvent): number | undefined {
    if (typeof event.date?.year === 'number' && Number.isFinite(event.date.year) && event.date.year > 0) return event.date.year;
    const value = event.date?.dateval?.[2];
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function collectEventOwners(people: RestPerson[], families: RestFamily[]): Map<string, RestPerson[]> {
    const peopleByHandle = new Map(people.filter((person) => person.handle).map((person) => [person.handle!, person]));
    const ownersByEvent = new Map<string, RestPerson[]>();
    const addOwner = (eventHandle: string, owner: RestPerson) => {
        const owners = ownersByEvent.get(eventHandle) ?? [];
        if (!owners.some((existing) => existing.handle === owner.handle)) {
            owners.push(owner);
            ownersByEvent.set(eventHandle, owners);
        }
    };

    for (const person of people) {
        for (const event of personExtended(person).events ?? []) {
            if (event.handle) addOwner(event.handle, person);
        }
    }
    for (const family of families) {
        const owners = [family.father_handle, family.mother_handle]
            .map((handle) => handle ? peopleByHandle.get(handle) : undefined)
            .filter((person): person is RestPerson => !!person);
        for (const event of familyExtended(family).events ?? []) {
            if (!event.handle) continue;
            owners.forEach((owner) => addOwner(event.handle!, owner));
        }
    }
    return ownersByEvent;
}

function featuresFor(events: AncestryEvent[], year?: number): GeoJSON.Feature[] {
    const byOwner = new Map<string, AncestryEvent[]>();
    for (const event of events) {
        if (!event.owner.handle) continue;
        const ownerEvents = byOwner.get(event.owner.handle) ?? [];
        ownerEvents.push(event);
        byOwner.set(event.owner.handle, ownerEvents);
    }

    const features: GeoJSON.Feature[] = [];
    for (const [ownerHandle, ownerEvents] of byOwner) {
        if (year !== undefined) {
            const firstYear = Math.min(...ownerEvents.map((event) => event.year));
            const lastYear = Math.max(...ownerEvents.map((event) => event.year));
            if (year < firstYear || year > lastYear + 10) continue;
        }

        const visible = (year === undefined ? [...ownerEvents] : ownerEvents.filter((event) => event.year <= year))
            .sort((a, b) => a.year - b.year);
        const latest = visible[visible.length - 1];
        if (!latest) continue;

        const properties = {
            title: restPersonName(latest.owner),
            xref_id: latest.event.gramps_id,
            person_id: latest.owner.gramps_id,
            owner_handle: ownerHandle,
        };
        const arcs = curvedStepFeatures(visible.map((event) => event.coordinates), properties);
        if (arcs.length > 0) {
            features.push(...arcs);
        } else {
            features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: latest.coordinates }, properties });
        }
    }
    return features;
}

function centerFrom(searchParams: URLSearchParams): Coordinates | undefined {
    const longitude = Number.parseFloat(searchParams.get('lon') ?? '');
    const latitude = Number.parseFloat(searchParams.get('lat') ?? '');
    return Number.isFinite(longitude) && Number.isFinite(latitude) ? [longitude, latitude] : undefined;
}

export function AncestryMap() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { mainId, setMainId, setSideItemId, treeName } = useViewRoute();
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
    const searchParamsRef = useRef(searchParams);
    const mapRef = useRef<mapboxgl.Map>(null);
    const mapViewportRef = useRef<HTMLDivElement>(null);
    const [initialView] = useState(() => {
        const zoomValue = Number.parseFloat(searchParams.get('zoom') ?? '');
        return {
            center: centerFrom(searchParams),
            zoom: Number.isFinite(zoomValue) ? zoomValue : undefined,
        };
    });
    const [selection, setSelection] = useState<{ personId: string | null; year?: number }>({ personId: null });
    const [mapViewportHeight, setMapViewportHeight] = useState(320);
    const [proximity, setProximity] = useState<{ lng: number; lat: number }>();
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; personId: string } | null>(null);

    useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    const rootQuery = useQuery({
        queryKey: ['person', treeName, mainId],
        queryFn: () => fetchPersonByGrampsId(mainId!, token),
        enabled: !!mainId,
    });
    const peopleQuery = useQuery({
        queryKey: ['ancestors', 'people', treeName, mainId],
        queryFn: () => fetchAncestorPersons(mainId!, token),
        enabled: !!mainId,
    });
    const familiesQuery = useQuery({
        queryKey: ['ancestors', 'families', treeName, mainId],
        queryFn: () => fetchAncestorFamilies(mainId!, token),
        enabled: !!mainId,
    });

    const people = useMemo(() => {
        const peopleByHandle = new Map<string, RestPerson>();
        for (const person of [...(peopleQuery.data ?? []), rootQuery.data].filter((person): person is RestPerson => !!person)) {
            if (person.handle) peopleByHandle.set(person.handle, person);
        }
        return [...peopleByHandle.values()];
    }, [peopleQuery.data, rootQuery.data]);
    const ownersByEvent = useMemo(() => collectEventOwners(people, familiesQuery.data ?? []), [people, familiesQuery.data]);
    const events = useMemo(() => {
        const handles = [...ownersByEvent.keys()].sort();
        return { handles, digest: handlesDigest(handles) };
    }, [ownersByEvent]);
    const eventsQuery = useQuery({
        queryKey: ['events', treeName, 'ancestry-map', events.digest],
        queryFn: () => fetchEventsByHandles(events.handles, token),
        enabled: events.handles.length > 0,
    });

    const ancestryEvents = useMemo(() => {
        const byHandle = new Map((eventsQuery.data ?? []).filter((event) => event.handle).map((event) => [event.handle!, event]));
        const result: AncestryEvent[] = [];
        for (const [handle, owners] of ownersByEvent) {
            const event = byHandle.get(handle);
            const coordinates = event ? coordinatesFor(event) : undefined;
            const year = event ? startYear(event) : undefined;
            if (!event || !coordinates || year === undefined) continue;
            owners.forEach((owner) => result.push({ event, owner, coordinates, year }));
        }
        return result;
    }, [eventsQuery.data, ownersByEvent]);

    const years = ancestryEvents.map((event) => event.year);
    const minYear = years.length ? Math.min(...years) : undefined;
    const maxYear = years.length ? Math.max(...years) : undefined;
    const selectedYear = selection.personId === mainId ? selection.year : maxYear;
    const features = useMemo(() => selectedYear === undefined ? [] : featuresFor(ancestryEvents, selectedYear), [ancestryEvents, selectedYear]);
    const bounds = useMemo(() => boundsOfFeatures(featuresFor(ancestryEvents)), [ancestryEvents]);

    const handleYearChange = useCallback((_event: Event, value: number | number[]) => {
        if (typeof value !== 'number') return;
        setSelection({ personId: mainId, year: value });
        const next = new URLSearchParams(searchParamsRef.current);
        next.set('year', value.toString());
        setSearchParams(next);
    }, [mainId, setSearchParams]);
    const onMoveEnd = useCallback((event: mapboxgl.MapboxEvent) => {
        const mapCenter = event.target.getCenter();
        setProximity({ lng: mapCenter.lng, lat: mapCenter.lat });
        const latitude = mapCenter.lat.toString();
        const longitude = mapCenter.lng.toString();
        const zoom = event.target.getZoom().toString();
        const current = searchParamsRef.current;
        if (current.get('lat') === latitude && current.get('lon') === longitude && current.get('zoom') === zoom) {
            return;
        }
        const next = new URLSearchParams(searchParamsRef.current);
        next.set('lat', latitude);
        next.set('lon', longitude);
        next.set('zoom', zoom);
        setSearchParams(next, { replace: true });
    }, [setSearchParams]);
    const onClick = useCallback((event: mapboxgl.MapMouseEvent) => {
        const features = event.target.queryRenderedFeatures(event.point, { layers: ['linestring-layer', 'markers'] });
        // Both lines and points carry person_id, so either kind of feature goes to the person.
        const personId = features.map((feature) => feature.properties?.person_id)
            .find((value): value is string => typeof value === 'string');
        if (personId) {
            setSideItemId(personId);
            return;
        }

        const eventId = features.map((feature) => feature.properties?.xref_id)
            .find((value): value is string => typeof value === 'string');
        if (eventId) setSideItemId(eventId);
    }, [setSideItemId]);
    const onContextMenu = useCallback((event: mapboxgl.MapMouseEvent) => {
        event.preventDefault();
        const features = event.target.queryRenderedFeatures(event.point, { layers: ['linestring-layer', 'markers'] });
        const personId = features.map((feature) => feature.properties?.person_id)
            .find((value): value is string => typeof value === 'string');
        if (!personId) return;
        setContextMenu({ mouseX: event.originalEvent.clientX, mouseY: event.originalEvent.clientY, personId });
    }, []);
    const initMap = useCallback((map: mapboxgl.Map) => {
        mapRef.current = map;
        map.on('moveend', onMoveEnd);
        map.on('click', onClick);
        map.on('contextmenu', onContextMenu);
        const mapCenter = map.getCenter();
        setProximity({ lng: mapCenter.lng, lat: mapCenter.lat });
    }, [onClick, onContextMenu, onMoveEnd]);

    useEffect(() => {
        const updateHeight = () => {
            const top = mapViewportRef.current?.getBoundingClientRect().top;
            if (top !== undefined) setMapViewportHeight(Math.max(240, Math.floor(window.innerHeight - top - 8)));
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [selectedYear]);

    const error = rootQuery.error ?? peopleQuery.error ?? familiesQuery.error ?? eventsQuery.error;
    const isLoading = rootQuery.isLoading || peopleQuery.isLoading || familiesQuery.isLoading || eventsQuery.isLoading;
    if (!mainId) return null;
    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error">Impossible de charger les données de migration.</Alert>;
    if (!rootQuery.data) return <Alert severity="warning">La personne demandée est introuvable.</Alert>;
    if (minYear === undefined || maxYear === undefined || selectedYear === undefined) {
        return <Alert severity="info">Aucun événement géolocalisé n'est disponible pour cette ascendance.</Alert>;
    }

    return <Box>
        {/* Short and small: the map is what is looked at. */}
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            Où vivaient les ascendants de <Box component="span" sx={{ color: 'secondary.main', fontWeight: 500 }}>{restPersonName(rootQuery.data)}</Box>.
            Faites glisser l'année, touchez une ligne ou un nom.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', px: 1 }}>
            <Typography component="span" sx={{ color: 'secondary.main', fontWeight: 700, minWidth: '2.6em' }}>{selectedYear}</Typography>
            <Slider track={false} size="small" aria-label="Année" valueLabelDisplay="auto" min={minYear} max={maxYear} value={selectedYear} onChange={handleYearChange} />
        </Stack>
        <Geocoding onLocationSelect={(latitude, longitude) => mapRef.current?.flyTo({ center: [longitude, latitude], zoom: 10 })} proximity={proximity} />
        <Box ref={mapViewportRef} sx={{ height: `${mapViewportHeight}px`, minHeight: 240 }}>
            <MapTiles features={features} bounds={bounds} zoom={initialView.zoom} center={initialView.center} onLoaded={initMap} markerLabelColor="#0b3d91" />
        </Box>
        {contextMenu && <PersonContextMenu
            anchorPosition={{ mouseX: contextMenu.mouseX, mouseY: contextMenu.mouseY }}
            personGrampsId={contextMenu.personId}
            treeName={treeName}
            token={token}
            onClose={() => setContextMenu(null)}
            onCenter={setMainId}
            onGoTo={setSideItemId}
        />}
    </Box>;
}
